import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, Calendar, DollarSign, Award } from 'lucide-react';
import axios from 'axios';
import { API } from '../../../App';
import SubscriptionWidget from '../SubscriptionWidget';

const OverviewTab = ({ stats }) => {
  const [expiringMembers, setExpiringMembers] = useState([]);

  useEffect(() => {
    const fetchExpiringMembers = async () => {
      try {
        const response = await axios.get(`${API}/dashboard/expiring-memberships`);
        setExpiringMembers(response.data);
      } catch (error) {
        console.error('Failed to fetch expiring memberships');
      }
    };
    fetchExpiringMembers();
  }, []);

  return (
    <div data-testid="overview-tab">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Dashboard Overview</h1>
      
      {stats?.expiring_memberships > 0 && (
        <Card className="border-l-4 border-orange-500 bg-gradient-to-r from-orange-50 to-white mb-6 shadow-sm" data-testid="expiring-alert">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="bg-orange-500 text-white rounded-full p-3 shadow-md">
                <Award className="h-5 w-5" />
              </div>
              <div>
                <p className="font-semibold text-orange-900">Memberships Expiring Soon</p>
                <p className="text-sm text-orange-700">{stats.expiring_memberships} membership(s) expire within 30 days</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card data-testid="stat-members" className="hover:shadow-lg transition-shadow duration-200 border-l-4 border-teal-500">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Members</CardTitle>
            <div className="bg-teal-100 p-2 rounded-lg">
              <Users className="h-5 w-5 text-teal-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">{stats?.total_members || 0}</div>
            <p className="text-xs text-gray-500 mt-1">All registered patients</p>
          </CardContent>
        </Card>

        <Card data-testid="stat-active" className="hover:shadow-lg transition-shadow duration-200 border-l-4 border-green-500">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Active Memberships</CardTitle>
            <div className="bg-green-100 p-2 rounded-lg">
              <Award className="h-5 w-5 text-green-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">{stats?.active_memberships || 0}</div>
            <p className="text-xs text-gray-500 mt-1">Currently active plans</p>
          </CardContent>
        </Card>

        <Card data-testid="stat-appointments" className="hover:shadow-lg transition-shadow duration-200 border-l-4 border-blue-500">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Upcoming Appointments</CardTitle>
            <div className="bg-blue-100 p-2 rounded-lg">
              <Calendar className="h-5 w-5 text-blue-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">{stats?.upcoming_appointments || 0}</div>
            <p className="text-xs text-gray-500 mt-1">Scheduled visits</p>
          </CardContent>
        </Card>

        <Card data-testid="stat-revenue" className="hover:shadow-lg transition-shadow duration-200 border-l-4 border-purple-500">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Monthly Revenue</CardTitle>
            <div className="bg-purple-100 p-2 rounded-lg">
              <DollarSign className="h-5 w-5 text-purple-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">${stats?.monthly_revenue?.toFixed(2) || '0.00'}</div>
            <p className="text-xs text-gray-500 mt-1">This month's income</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="shadow-sm lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-xl">Quick Stats</CardTitle>
            <CardDescription>System overview and performance metrics</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-700 font-medium">Total Appointments</span>
                <span className="font-bold text-2xl text-gray-900">{stats?.total_appointments || 0}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-700 font-medium">Active Members Rate</span>
                <span className="font-bold text-2xl text-gray-900">
                  {stats?.total_members > 0 ? ((stats.active_memberships / stats.total_members) * 100).toFixed(1) : 0}%
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Subscription Widget */}
        <SubscriptionWidget />
      </div>
    </div>
  );
};

export default OverviewTab;
